/**
 * Period bucketing for the Order History summary. Separate from the
 * component so the module isn't a mixed component/helper export (which
 * breaks React Fast Refresh) and so the bucketing is unit-testable on
 * its own.
 */

export type Granularity = 'month' | 'quarter' | 'year'

/** Bucket width that keeps a window readable — roughly 6-24 columns. */
export function granularityFor(from: string, to: string): Granularity {
  const months =
    (new Date(to).getFullYear() - new Date(from).getFullYear()) * 12 +
    (new Date(to).getMonth() - new Date(from).getMonth())
  // 24 monthly columns still reads fine, and monthly is the granularity
  // where reorder cadence shows up — worth preferring over quarters.
  if (months <= 24) return 'month'
  if (months <= 72) return 'quarter'
  return 'year'
}

/** `2026-03-14` becomes `2026-03` | `2026-Q1` | `2026`. Sorts lexically. */
export function periodKey(iso: string, g: Granularity): string {
  const [y, m] = iso.split('-')
  if (g === 'year') return y!
  if (g === 'quarter') return `${y}-Q${Math.floor((Number(m) - 1) / 3) + 1}`
  return `${y}-${m}`
}

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export function periodLabel(key: string, g: Granularity): string {
  if (g === 'year') return key
  if (g === 'quarter') return key.replace('-', ' ')
  const [y, m] = key.split('-')
  return `${MONTH_LABELS[Number(m) - 1]} ${y!.slice(2)}`
}

export function money(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}
